import BlobDigester from './blob-digester';
import ImageUrl from './image-url';
import { ImageResource } from './image-resource';
import SparkMD5 from 'spark-md5';
import ImageConverter from './image-converter';

/**
 * 图片秒看功能的核心业务逻辑，
 * 用处是在图片上传完成前，保证图片内容能够可以显示、编辑、复制粘贴，
 * 同时在协同时，也能保证图片正常显示
 */
class ImageQuickLookManager {
    private static instance?: ImageQuickLookManager;

    public static getInstance(): ImageQuickLookManager {
        if (!this.instance) {
            this.instance = new this();
        }
        return this.instance;
    }

    private resources: Map<string, ImageResource>;
    private indexByURL: Map<string, string>;

    public constructor() {
        this.resources = new Map();
        this.indexByURL = new Map();
    }

    /**
     * 根据数据 hash 生成一个 resource id，
     * 命中缓存的话，可能会复用之前的 ID
     */
    public createResourceId(data: string): string {
        const relId = this.getRelationshipId(SparkMD5.hash(data));
        // 存在缓存，则直接复用 ID，防止生成冗余的 ID
        // （使用 base64 做 hash 的时候，会命中 blob 生成的 hash id）
        const cache = this.resources.get(relId);
        if (cache) {
            return cache.id;
        }
        return relId;
    }

    /**
     * 根据数据 blob 生成一个 resource id，
     */
    public async createResourceIdByBlob(blob: Blob): Promise<string> {
        return this.getRelationshipId(await BlobDigester.digestMD5(blob));
    }

    public async addImageWithCDNUrl(cdnUrl: string, id?: string): Promise<ImageResource> {
        // TODO: 本地编辑需要调用端能力来获取 Blob，否则可能会出现跨域问题
        const blob = await fetch(cdnUrl).then(res => res.blob());
        return this.addImageBlob(blob, id);
    }

    /**
     * 通过 base64 DataUri 添加一张图到秒看管理器
     * 默认会通过 DataUri hash 产生一个资源 id
     */
    public async addImageWithBase64Url(dataUri: string, id?: string): Promise<ImageResource> {
        const resourceId = id === undefined ? this.createResourceId(dataUri) : id;
        const blob = ImageConverter.convertDataUriToBlob(dataUri);
        if (!blob) {
            throw new Error('Failed to convert data uri to blob');
        }
        return this.addImageBlob(blob, resourceId);
    }

    /**
     * 通过 Blob 对象 添加一张图到秒看管理器，
     * 默认会通过 Blob 二进制数据 hash 产生一个资源 id
     */
    public async addImageBlob(blob: Blob, id?: string): Promise<ImageResource> {
        const resourceId = id === undefined ? (await this.createResourceIdByBlob(blob)) : id;
        const cache = this.resources.get(resourceId);
        if (cache) {
            return cache;
        }

        const resource = new ImageResource(resourceId, blob);
        await Promise.all([resource.tryLoadMeta(), resource.loadBase64Url()]);
        this.resources.set(resourceId, resource);
        // 为 base64 建立索引，加速本地复制粘贴
        this.resources.set(this.createResourceId(resource.getBase64Url()), resource);
        this.indexByURL.set(resource.getRawQuickLockUrl(), resourceId);

        resource.onDidUpload((resource) => {
            this.indexByURL.set(resource.getCdnUrl(), resourceId);
            resource.clearBase64Url();
        });

        return resource;
    }

    /**
     * 批量添加 Blob 对象到秒看管理器
     */
    public async bulkAddImageBlobs(blobs: Blob[], ids?: string[]): Promise<ImageResource[]> {
        return Promise.all(blobs.map((blob, i) => this.addImageBlob(blob, ids?.[i])));
    }

    /**
     * 通过 URL 地址，反查 ImageResource，
     * 可以实现根据 cdn 地址获取秒看地址等操作
     */
    public getResourceByUrl(url: string): ImageResource | undefined {
        const id = this.indexByURL.get(ImageUrl.stripAllTags(url)) ?? url;
        return this.resources.get(id as string);
    }

    /**
     * 去除 URL 的各种附加参数，确保能正常显示，同时复用 BlobUrl 防止闪烁，
     * 解决 UI 层的 BlobUrl 上附加了宽高参数，在旧版 Safari 中加载不出来的问题，
     * 编辑面板的图片，并没有走 Canvas 或者 Dom 的渲染层逻辑来渲染，所以需要特殊处理，
     * 更好的模式是重构一下，图片的元信息通过额外的缓存器来维护
     */
    public getDisplayUrl(url: string): string {
        const resource = this.getResourceByUrl(url);
        if (resource) {
            return ImageUrl.stripAllTags(resource.getQuickLookUrl());
        }

        if (ImageUrl.isBlobUrl(url)) {
            return ImageUrl.stripAllTags(url);
        }
        return this.getDowngradedImageSrcIfNeeded(url);
    }

    /**
     * 根据 SDK 来给出 UI 层的 src，规则是
     * 1. 优先使用本地的 BlobUrl；
     * 2. 如果协同端没有上传成功，则隐藏图片的展示；
     */
    public getNextUIDataFromSDKSrc(sdkSrc?: string): { src?: string; forceHidden?: boolean } {
        if (!sdkSrc) {
            return { src: sdkSrc, forceHidden: false };
        }

        const sdkCache = ImageQuickLookManager.getInstance().getResourceByUrl(sdkSrc);
        const forceHidden = ImageUrl.isRelationshipUrl(sdkSrc) && !sdkCache;
        const src = sdkCache?.getQuickLookUrl() ?? sdkSrc;

        return {
            src: forceHidden ? '' : this.getDowngradedImageSrcIfNeeded(src),
            forceHidden,
        };
    }

    /**
     * 将 URL 与特定的 ImageResource 关联，
     * 这样可以通过 url 反向获取到 ImageResource，从而拿到秒看地址
     */
    public mapResourceToUrl(resource: ImageResource, url: string): void {
        this.indexByURL.set(ImageUrl.stripAllTags(url), resource.id);
    }

    // 与 SDK 约定的 ID 格式，
    // SDK 会自动创建监听器，在 src 更新时，通知受影响的 UI 刷新
    private getRelationshipId(rawId: string) {
        return `rel:${rawId}`;
    }

    private getDowngradedImageSrcIfNeeded(src: string) {
        // 桌面端本地编辑请求 EMF 图片，请求降级转换为 SVG 进行支持
        if (src.endsWith('.emf')) {
            return src.replace(/emf$/, 'emf.svg');
        }

        return src;
    }
}

export default ImageQuickLookManager;
